import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import MarketingShell from "@/components/MarketingShell";
import {
  Mail, Plus, Search, Edit, Copy, Archive,
  Send, Users, ChevronRight, ChevronLeft, Loader2, X,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CFG = {
  draft: { label: "Draft", cls: "tag-amber" },
  scheduled: { label: "Scheduled", cls: "tag-purple" },
  sending: { label: "Sending", cls: "tag-blue" },
  active: { label: "Active", cls: "tag-blue" },
  sent: { label: "Sent", cls: "tag-green" },
  paused: { label: "Paused", cls: "tag-gray" },
  archived: { label: "Archived", cls: "tag-gray" },
};

export default function MarketingCampaigns() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({ type: "email", audience: "", template: "", subject: "", scheduleDate: "", scheduleTime: "" });
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // The endpoint can return either a flat list (legacy) or a paginated
      // envelope `{items, total, page, ...}`. Handle both.
      const rows = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      const mapped = rows.map((c) => {
        const m = c.metrics || {};
        const opened = m.opened || 0;
        const sent = m.sent || 0;
        const clicked = m.clicked || 0;
        return {
          id: c.id,
          name: c.name || "Untitled campaign",
          subtitle: c.content ? c.content.slice(0, 60) : (STATUS_CFG[c.status]?.label || ""),
          type: c.type || "email",
          audience: (c.target_segment && c.target_segment.join(", ")) || "—",
          audienceCount: Array.isArray(c.target_segment) ? c.target_segment.length : 0,
          status: c.status || "draft",
          sentDate: c.start_date || "",
          openRate: sent > 0 ? (opened / sent) * 100 : 0,
          clickRate: sent > 0 ? (clicked / sent) * 100 : 0,
        };
      });
      setCampaigns(mapped);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const filters = [
    { key: "all", label: "All" },
    { key: "email", label: "Email" },
    { key: "sequence", label: "Sequence" },
    { key: "draft", label: "Draft" },
    { key: "scheduled", label: "Scheduled" },
    { key: "sent", label: "Sent" },
    { key: "archived", label: "Archived" },
  ];

  const filtered = campaigns.filter(c => {
    if (filter === "all") return true;
    if (["email", "sequence"].includes(filter)) return c.type === filter;
    return c.status === filter;
  }).filter(c => !search || (c.name || "").toLowerCase().includes(search.toLowerCase()));

  const activeCount = campaigns.filter(c => ["sending", "scheduled", "active"].includes(c.status)).length;

  const createCampaign = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/campaigns`,
        {
          name: wizardData.subject || "New campaign",
          type: wizardData.type || "email",
          status: wizardData.scheduleDate ? "scheduled" : "draft",
          target_segment: wizardData.audience ? [wizardData.audience] : [],
          start_date: wizardData.scheduleDate || null,
          content: wizardData.subject || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Campaign created");
      setShowWizard(false);
      setWizardStep(1);
      setWizardData({ type: "email", audience: "", template: "", subject: "", scheduleDate: "", scheduleTime: "" });
      loadCampaigns();
    } catch {
      toast.error("Could not create campaign — try again");
    } finally {
      setSubmitting(false);
    }
  };

  const headerActions = (
    <button className="btn btn-primary" onClick={() => { setShowWizard(true); setWizardStep(1); }} data-testid="new-campaign-btn">
      <Plus style={{ width: 13, height: 13 }} /> New Campaign
    </button>
  );

  if (loading) {
    return (
      <MarketingShell title="Campaigns" subtitle="Loading…">
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }} data-testid="campaigns-loading">
          <Loader2 style={{ width: 24, height: 24, animation: "spin 1s linear infinite", color: "var(--blue)" }} />
        </div>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell title="Campaigns" subtitle={campaigns.length === 0 ? "No campaigns yet" : `${campaigns.length} total · ${activeCount} active`} actions={headerActions}>
      <div data-testid="marketing-campaigns">
        {/* Filters */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div className="mkt-segmented">
            {filters.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={filter === f.key ? "active" : ""}
                data-testid={`filter-${f.key}`}>{f.label}</button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "var(--text-tertiary)" }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search campaigns…" style={{ paddingLeft: 30, fontSize: 12, width: 220, height: 30 }} className="input" data-testid="search-campaigns" />
          </div>
        </div>

        {/* Campaign Table */}
        <div className="mkt-table-wrap">
          <table className="mkt-table">
            <thead>
              <tr>
                {["Campaign", "Type", "Audience", "Status", "Date", "Open rate", "Click rate", ""].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const st = STATUS_CFG[c.status] || STATUS_CFG.draft;
                return (
                  <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 540, color: "var(--text-primary)" }}>{c.name}</div>
                      <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>{c.subtitle}</div>
                    </td>
                    <td>
                      <span className={`tag ${c.type === "email" ? "tag-blue" : "tag-teal"}`}>
                        {c.type === "email" ? "Email" : "Sequence"}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{c.audience}</div>
                      <div className="num" style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>{c.audienceCount} contacts</div>
                    </td>
                    <td>
                      <span className={`tag ${st.cls}`}>{st.label}</span>
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>{c.sentDate || "—"}</td>
                    <td>
                      {c.openRate > 0 ? (
                        <div>
                          <div className="num" style={{ fontSize: 12, fontWeight: 540 }}>{c.openRate}%</div>
                          <div style={{ height: 4, width: 60, background: "var(--subtle)", borderRadius: 2, marginTop: 3 }}>
                            <div style={{ height: "100%", width: `${c.openRate}%`, background: "var(--blue)", borderRadius: 2 }} />
                          </div>
                        </div>
                      ) : <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>—</span>}
                    </td>
                    <td className="num" style={{ fontSize: 12, color: "var(--text-secondary)" }}>{c.clickRate > 0 ? `${c.clickRate}%` : "—"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }} title="Edit"><Edit style={{ width: 13, height: 13 }} /></button>
                        <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }} title="Duplicate"><Copy style={{ width: 13, height: 13 }} /></button>
                        <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }} title="Archive"><Archive style={{ width: 13, height: 13 }} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>
              {campaigns.length === 0
                ? "No campaigns yet — click \u201CNew Campaign\u201D to create your first one."
                : "No campaigns match your filter"}
            </div>
          )}
        </div>

      {/* New Campaign Wizard */}
      {showWizard && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowWizard(false)}>
          <div style={{ background: "var(--surface)", borderRadius: 12, width: 520, maxHeight: "80vh", overflow: "auto", padding: "24px 28px" }} onClick={e => e.stopPropagation()} data-testid="campaign-wizard">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 650 }}>New Campaign — Step {wizardStep} of 5</h2>
              <button onClick={() => setShowWizard(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}><X style={{ width: 16, height: 16 }} /></button>
            </div>

            {/* Step progress */}
            <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
              {[1,2,3,4,5].map(s => (
                <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: s <= wizardStep ? "var(--blue)" : "var(--gray-light)" }} />
              ))}
            </div>

            {wizardStep === 1 && (
              <div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>Choose campaign type</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    { key: "email", label: "Email Campaign", desc: "Send to a segment once", icon: Mail },
                    { key: "sequence", label: "Email Sequence", desc: "Automated multi-step series", icon: Send },
                  ].map(t => (
                    <button key={t.key} onClick={() => setWizardData({...wizardData, type: t.key})}
                      style={{ padding: 18, borderRadius: 8, border: `1.5px solid ${wizardData.type === t.key ? "var(--blue)" : "var(--border)"}`, background: wizardData.type === t.key ? "var(--blue-light)" : "var(--surface)", cursor: "pointer", textAlign: "left" }}
                      data-testid={`wizard-type-${t.key}`}>
                      <t.icon style={{ width: 20, height: 20, color: "var(--blue)", marginBottom: 8 }} />
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {wizardStep === 2 && (
              <div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>Select audience</p>
                {["All Contacts", "Hot Leads", "Cardiology", "Demo Stage", "CCM Interest", "Live Clients", "New Leads — Last 30 Days"].map(seg => (
                  <button key={seg} onClick={() => setWizardData({...wizardData, audience: seg})}
                    style={{ display: "block", width: "100%", padding: "10px 14px", marginBottom: 6, borderRadius: 6, border: `1px solid ${wizardData.audience === seg ? "var(--blue)" : "var(--border)"}`, background: wizardData.audience === seg ? "var(--blue-light)" : "var(--surface)", cursor: "pointer", textAlign: "left", fontSize: 13, color: "var(--text-primary)" }}
                    data-testid={`wizard-audience-${seg.replace(/\s/g, '-').toLowerCase()}`}>
                    <Users style={{ width: 13, height: 13, display: "inline", marginRight: 8, verticalAlign: "middle" }} /> {seg}
                  </button>
                ))}
              </div>
            )}

            {wizardStep === 3 && (
              <div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>Campaign details</p>
                <label style={{ fontSize: 11, color: "var(--text-tertiary)", display: "block", marginBottom: 4 }}>Subject line</label>
                <input className="input" placeholder="Enter email subject..." value={wizardData.subject} onChange={e => setWizardData({...wizardData, subject: e.target.value})} style={{ marginBottom: 14 }} data-testid="wizard-subject" />
                <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>You'll build the email content in the Email Builder after creating this campaign.</p>
              </div>
            )}

            {wizardStep === 4 && (
              <div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>Schedule</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, color: "var(--text-tertiary)", display: "block", marginBottom: 4 }}>Date</label>
                    <input type="date" className="input" value={wizardData.scheduleDate} onChange={e => setWizardData({...wizardData, scheduleDate: e.target.value})} data-testid="wizard-date" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: "var(--text-tertiary)", display: "block", marginBottom: 4 }}>Time</label>
                    <input type="time" className="input" value={wizardData.scheduleTime} onChange={e => setWizardData({...wizardData, scheduleTime: e.target.value})} data-testid="wizard-time" />
                  </div>
                </div>
              </div>
            )}

            {wizardStep === 5 && (
              <div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>Review and confirm</p>
                <div style={{ background: "var(--page-bg)", borderRadius: 8, padding: 16, fontSize: 12, lineHeight: 2 }}>
                  <div><strong>Type:</strong> {wizardData.type === "email" ? "Email Campaign" : "Email Sequence"}</div>
                  <div><strong>Audience:</strong> {wizardData.audience || "Not selected"}</div>
                  <div><strong>Subject:</strong> {wizardData.subject || "Not set"}</div>
                  <div><strong>Schedule:</strong> {wizardData.scheduleDate ? `${wizardData.scheduleDate} at ${wizardData.scheduleTime || "TBD"}` : "Not scheduled"}</div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
              <button onClick={() => wizardStep > 1 ? setWizardStep(wizardStep - 1) : setShowWizard(false)}
                className="btn" style={{ fontSize: 12, padding: "7px 14px", border: "0.5px solid var(--border)" }}>
                {wizardStep > 1 ? <><ChevronLeft style={{ width: 13, height: 13 }} /> Back</> : "Cancel"}
              </button>
              <button onClick={() => {
                if (wizardStep < 5) {
                  setWizardStep(wizardStep + 1);
                } else {
                  createCampaign();
                }
              }} className="btn btn-primary" style={{ fontSize: 12, padding: "7px 14px" }} data-testid="wizard-next" disabled={submitting}>
                {wizardStep < 5
                  ? <>Next <ChevronRight style={{ width: 13, height: 13 }} /></>
                  : (submitting ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> : "Create Campaign")}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </MarketingShell>
  );
}
